import React, { useState } from "react";
import { usePosts } from "../../lib/firebase/post/read";
import { Link } from "react-router-dom";
import {
  Table,
  Button,
  Spinner,
  Image,
  Pagination,
  Form,
} from "react-bootstrap";
import { CirclePlus } from "lucide-react";

export default function PostListView() {
  const {
    data: postsData,
    error: postsError,
    isLoading: postsLoading,
  } = usePosts("posts");
  const {
    data: magazinesData,
    error: magazinesError,
    isLoading: magazinesLoading,
  } = usePosts("magazines");

  const [postsSortDirection, setPostsSortDirection] = useState("desc");
  const [magazinesSortDirection, setMagazinesSortDirection] = useState("desc");
  const [postsCurrentPage, setPostsCurrentPage] = useState(1);
  const [magazinesCurrentPage, setMagazinesCurrentPage] = useState(1);
  const [postsSearchTerm, setPostsSearchTerm] = useState("");
  const [magazinesSearchTerm, setMagazinesSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5); // Default: 5 rows per page

  if (postsLoading || magazinesLoading) {
    return (
      <Spinner
        animation="border"
        variant="primary"
        className="d-block mx-auto"
      />
    );
  }
  if (postsError || magazinesError) {
    return (
      <h1 className="text-danger text-center">
        {postsError || magazinesError}
      </h1>
    );
  }
  if (!postsData || !magazinesData) {
    return <h1 className="text-warning text-center">Data not found!</h1>;
  }

  const filterAndSortData = (data, searchTerm, sortDirection) => {
    let filteredData = data.filter((item) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        item.title?.toLowerCase().includes(searchLower) ||
        item.slug?.toLowerCase().includes(searchLower)
      );
    });

    filteredData.sort((a, b) => {
      const dateA = a.timestamp ? new Date(a.timestamp.toDate()) : new Date(0);
      const dateB = b.timestamp ? new Date(b.timestamp.toDate()) : new Date(0);
      return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
    });

    return filteredData;
  };

  const postsFilteredData = filterAndSortData(
    postsData,
    postsSearchTerm,
    postsSortDirection
  );
  const magazinesFilteredData = filterAndSortData(
    magazinesData,
    magazinesSearchTerm,
    magazinesSortDirection
  );

  const paginateData = (data, currentPage) => {
    const totalRows = data.length;
    const totalPages = Math.ceil(totalRows / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
    const paginatedData = data.slice(startIndex, endIndex);
    return { paginatedData, totalPages, startIndex, totalRows };
  };

  const {
    paginatedData: paginatedPosts,
    totalPages: postsTotalPages,
    startIndex: postsStartIndex,
    totalRows: postsTotalRows,
  } = paginateData(postsFilteredData, postsCurrentPage);
  const {
    paginatedData: paginatedMagazines,
    totalPages: magazinesTotalPages,
    startIndex: magazinesStartIndex,
    totalRows: magazinesTotalRows,
  } = paginateData(magazinesFilteredData, magazinesCurrentPage);

  const handleDateSort = (type) => {
    if (type === "posts") {
      setPostsSortDirection(postsSortDirection === "desc" ? "asc" : "desc");
      setPostsCurrentPage(1);
    } else {
      setMagazinesSortDirection(
        magazinesSortDirection === "desc" ? "asc" : "desc"
      );
      setMagazinesCurrentPage(1);
    }
  };

  const handlePageChange = (type, page) => {
    if (type === "posts") {
      setPostsCurrentPage(page);
    } else {
      setMagazinesCurrentPage(page);
    }
  };

  const handleRowsPerPageChange = (type, e) => {
    const newRowsPerPage = parseInt(e.target.value);
    setRowsPerPage(newRowsPerPage);
    if (type === "posts") {
      setPostsCurrentPage(1);
    } else {
      setMagazinesCurrentPage(1);
    }
  };

  const getPaginationItems = (type, currentPage, totalPages) => {
    const items = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    items.push(
      <Pagination.First
        onClick={() => handlePageChange(type, 1)}
        disabled={currentPage === 1}
        key={`${type}-first`}
      />
    );

    items.push(
      <Pagination.Prev
        onClick={() => handlePageChange(type, currentPage - 1)}
        disabled={currentPage === 1}
        key={`${type}-prev`}
      />
    );

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={`${type}-${page}`}
          active={page === currentPage}
          onClick={() => handlePageChange(type, page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        onClick={() => handlePageChange(type, currentPage + 1)}
        disabled={currentPage === totalPages}
        key={`${type}-next`}
      />
    );

    items.push(
      <Pagination.Last
        onClick={() => handlePageChange(type, totalPages)}
        disabled={currentPage === totalPages}
        key={`${type}-last`}
      />
    );

    return items;
  };

  const renderTable = (
    data,
    type,
    searchTerm,
    setSearchTerm,
    sortDirection,
    currentPage,
    totalPages,
    startIndex,
    totalRows
  ) => {
    return (
      <>
        <div className="d-flex justify-content-between align-items-center gap-4 mb-3">
          <h3>{type === "posts" ? "Posts" : "Magazines"}</h3>
          <Form className="flex-grow-1">
            <Form.Group controlId={`${type}-search`}>
              <Form.Control
                type="text"
                placeholder={`Search ${
                  type === "posts" ? "Posts" : "Magazines"
                } by title or slug`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-100"
              />
            </Form.Group>
          </Form>
          {type === "posts" && (
            <Link to="/admin/posts/form">
              <Button
                variant="primary"
                className="d-flex gap-2 align-items-center rounded-pill px-4 py-2 font-weight-bold"
              >
                <CirclePlus />
                Add
              </Button>
            </Link>
          )}
        </div>
        <Table striped bordered hover responsive>
          <thead className="bg-primary text-white">
            <tr className="text-center">
              <th>Sr.</th>
              <th>Image</th>
              <th>Title</th>
              <th>Slug</th>
              <th>Category</th>
              <th
                onClick={() => handleDateSort(type)}
                style={{ cursor: "pointer" }}
              >
                Date {sortDirection === "desc" ? "↓" : "↑"}
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center">
                  No {type === "posts" ? "Posts" : "Magazines"} Found
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <tr key={index} className="text-center">
                  <td>{startIndex + index + 1}</td>
                  <td>
                    <Image
                      src={item?.imageURL}
                      height={90}
                      width={150}
                      rounded
                    />
                  </td>
                  <td>{item?.title}</td>
                  <td>{item?.slug}</td>
                  <td>{item?.categoryId}</td>
                  <td>
                    {item?.timestamp
                      ? new Date(item.timestamp.toDate()).toLocaleDateString()
                      : "N/A"}
                  </td>
                  <td>
                    <div className="d-flex justify-content-center gap-2">
                      <Link
                        to={`/admin/posts/form?id=${encodeURIComponent(
                          item?.id
                        )}&collection=${type}`}
                      >
                        <Button variant="primary" size="sm">
                          Action
                        </Button>
                      </Link>
                      {type === "magazines" && item?.pdfURL && (
                        <a
                          href={item.pdfURL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="info" size="sm">
                            View PDF
                          </Button>
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        {data.length > 0 && (
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">
            <div className="mb-3 mb-md-0">
              <Form.Group className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0">Rows per page:</Form.Label>
                <Form.Select
                  value={rowsPerPage}
                  onChange={(e) => handleRowsPerPageChange(type, e)}
                  style={{ width: "auto" }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="d-flex flex-column flex-sm-row align-items-center gap-3">
              <span>
                Showing {startIndex + 1} to{" "}
                {Math.min(startIndex + rowsPerPage, totalRows)} of {totalRows}{" "}
                entries
              </span>
              <Pagination className="mb-0">
                {getPaginationItems(type, currentPage, totalPages)}
              </Pagination>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <section>
      {renderTable(
        paginatedPosts,
        "posts",
        postsSearchTerm,
        setPostsSearchTerm,
        postsSortDirection,
        postsCurrentPage,
        postsTotalPages,
        postsStartIndex,
        postsTotalRows
      )}
      <hr className="my-5" />
      {renderTable(
        paginatedMagazines,
        "magazines",
        magazinesSearchTerm,
        setMagazinesSearchTerm,
        magazinesSortDirection,
        magazinesCurrentPage,
        magazinesTotalPages,
        magazinesStartIndex,
        magazinesTotalRows
      )}
    </section>
  );
}
